import {
  ArrowsVertical,
  DragHorizontal,
  Pin,
  PinFilled,
  SortAscending,
  SortDescending,
} from '@carbon/react/icons';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { flexRender, Header, Table } from '@tanstack/react-table';
import classNames from 'classnames';
import { Fragment, useCallback, useEffect, useRef } from 'react';
import { useDataTableContext } from './context';
import { DataTableColumnMeta } from './types';
import styles from './table-header.module.scss';
import { FilterRow } from './filter-row';
import {
  getAlignment,
  getPinningStyles,
  RESERVED_COLUMN_IDS,
  titleFromText,
} from './utils';

// ─────────────────────────────────────
// Sortable Header Cell
// ─────────────────────────────────────

/**
 * Props for the SortableHeaderCell component.
 */
interface SortableHeaderCellProps<TData, TValue> {
  header: Header<TData, TValue>;
  enableSorting?: boolean;
  enableColumnReordering?: boolean;
  enableColumnPinning?: boolean;
  enableColumnResizing?: boolean;
  /** Center the header label instead of following the column's data alignment. */
  centerHeaders?: boolean;
  effectiveWidth?: number;
  onColumnResize?: (columnId: string, width: number) => void;
}

/**
 * Draggable table header cell with sorting, pinning, and resize functionality.
 * Uses @dnd-kit for drag-and-drop reordering capabilities.
 * @param {SortableHeaderCellProps} props - Component props
 * @param {Header} props.header - TanStack Table header instance
 * @param {boolean} [props.enableSorting] - Enable sort controls (default: true)
 * @param {boolean} [props.enableColumnPinning] - Enable pin controls (default: true)
 * @param {boolean} [props.enableColumnResizing] - Enable resize handle (default: true)
 * @return {JSX.Element} The header cell element
 */
function SortableHeaderCell<TData, TValue>({
  header,
  enableSorting = true,
  enableColumnReordering = true,
  enableColumnPinning = true,
  enableColumnResizing = true,
  centerHeaders = false,
  effectiveWidth,
  onColumnResize,
}: SortableHeaderCellProps<TData, TValue>) {
  const meta = header.column.columnDef.meta as DataTableColumnMeta | undefined;
  // A pinned column's placement is governed by the pinning array, not the column order, so
  // dragging it is a no-op — don't offer the drag handle on pinned columns.
  const canReorder =
    enableColumnReordering &&
    meta?.enableReordering !== false &&
    !header.column.getIsPinned();
  const canPin = enableColumnPinning && meta?.enablePinning !== false;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: header.column.id,
    disabled: !canReorder,
  });

  // Use effectiveWidth if provided (for independent resize), otherwise fallback to header.getSize()
  const columnWidth = effectiveWidth ?? header.getSize();

  const pinStyles = getPinningStyles(header.column);
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    width: columnWidth,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1 : pinStyles.zIndex,
    position: pinStyles.position as React.CSSProperties['position'],
    left: pinStyles.left,
    right: pinStyles.right,
    boxShadow: pinStyles.boxShadow,
  };

  // Track cleanup function for resize listeners to prevent memory leaks on unmount
  const resizeCleanupRef = useRef<(() => void) | null>(null);

  // Cleanup resize listeners on unmount
  useEffect(() => {
    return () => {
      resizeCleanupRef.current?.();
    };
  }, []);

  // Custom resize handler that tracks mouse movement and calls onColumnResize
  const handleResize = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!onColumnResize) {
        // Fall back to TanStack's default resize handler
        header.getResizeHandler()(e);
        return;
      }

      e.preventDefault();
      const startX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const startWidth = columnWidth;

      const handleMouseMove = (moveEvent: MouseEvent | TouchEvent) => {
        const clientX =
          'touches' in moveEvent
            ? moveEvent.touches[0].clientX
            : moveEvent.clientX;
        const delta = clientX - startX;
        const newWidth = Math.max(50, startWidth + delta); // Minimum 50px
        onColumnResize(header.column.id, newWidth);
      };

      const cleanup = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleMouseMove);
        document.removeEventListener('touchend', handleMouseUp);
        resizeCleanupRef.current = null;
      };

      const handleMouseUp = () => {
        cleanup();
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleMouseMove);
      document.addEventListener('touchend', handleMouseUp);

      // Store cleanup function for unmount
      resizeCleanupRef.current = cleanup;
    },
    [columnWidth, header, onColumnResize]
  );

  const canSort = enableSorting && header.column.getCanSort();
  const sortDirection = header.column.getIsSorted();
  const isPinned = header.column.getIsPinned();
  // Header label alignment. `centerHeaders` centers every header regardless of the
  // column's data alignment; otherwise follow the column type (numeric → right etc.).
  const alignment = centerHeaders ? 'center' : getAlignment(meta);

  const isSelectColumn = header.column.id === RESERVED_COLUMN_IDS.SELECT;
  const isExpanderColumn = header.column.id === RESERVED_COLUMN_IDS.EXPANDER;

  return (
    <th
      ref={setNodeRef}
      data-column-id={header.column.id}
      style={style}
      className={classNames(styles.tableHeaderCell, {
        [styles.tableHeaderCellDragging]: isDragging,
        [styles.tableHeaderCellPinned]: isPinned,
        [styles.tableHeaderCellSelect]: isSelectColumn,
        [styles.tableHeaderCellExpander]: isExpanderColumn,
      })}
    >
      <div className={styles.tableHeaderCellContent}>
        {/* Column label */}
        <div
          data-column-content
          className={classNames(styles.tableHeaderLabel, {
            [styles.tableHeaderLabelSortable]: canSort,
          })}
          style={{ textAlign: alignment }}
          onMouseEnter={titleFromText}
          onClick={
            canSort ? header.column.getToggleSortingHandler() : undefined
          }
        >
          {header.isPlaceholder
            ? null
            : flexRender(header.column.columnDef.header, header.getContext())}
        </div>

        {/* Header actions */}
        <div data-column-actions className={styles.tableHeaderActions}>
          {/* Sort indicator */}
          {canSort && (
            <button
              type="button"
              className={classNames(styles.tableHeaderAction, {
                [styles.tableHeaderActionActive]: sortDirection,
              })}
              onClick={header.column.getToggleSortingHandler()}
              title="Sort"
            >
              {sortDirection === 'asc' ? (
                <SortAscending />
              ) : sortDirection === 'desc' ? (
                <SortDescending />
              ) : (
                <ArrowsVertical />
              )}
            </button>
          )}

          {/* Drag handle */}
          {canReorder && (
            <button
              type="button"
              className={styles.tableHeaderAction}
              {...attributes}
              {...listeners}
              title="Drag to reorder"
            >
              <DragHorizontal />
            </button>
          )}

          {/* Pin button */}
          {canPin && (
            <button
              type="button"
              className={classNames(styles.tableHeaderAction, {
                [styles.tableHeaderActionActive]: isPinned,
              })}
              onClick={() => {
                if (isPinned) {
                  header.column.pin(false);
                } else {
                  header.column.pin('left');
                }
              }}
              title={isPinned ? 'Unpin column' : 'Pin column'}
            >
              {isPinned ? <PinFilled /> : <Pin />}
            </button>
          )}
        </div>

        {/* Resize handle */}
        {enableColumnResizing && header.column.getCanResize() && (
          <div
            className={classNames(styles.tableHeaderResizer, {
              [styles.tableHeaderResizerActive]: header.column.getIsResizing(),
            })}
            onMouseDown={handleResize}
            onTouchStart={handleResize}
          />
        )}
      </div>
    </th>
  );
}

// ─────────────────────────────────────
// Table Header Props
// ─────────────────────────────────────

interface TableHeaderProps<TData> {
  table: Table<TData>;
  enableSorting?: boolean;
  enableColumnReordering?: boolean;
  enableColumnPinning?: boolean;
  enableColumnResizing?: boolean;
  /** Center every column header label regardless of the column's data alignment. */
  centerHeaders?: boolean;
  effectiveWidths?: Record<string, number>;
  onColumnResize?: (columnId: string, width: number) => void;
}

// ─────────────────────────────────────
// Table Header Component
// ─────────────────────────────────────

/**
 * Main table header component with drag-and-drop column reordering.
 * Renders header cells with sort, pin, and resize controls.
 * Optionally shows inline column filters via FilterRow.
 * @param {TableHeaderProps} props - Component props
 * @param {Table} props.table - TanStack Table instance
 * @param {boolean} [props.enableSorting] - Enable sorting (default: true)
 * @param {boolean} [props.enableColumnReordering] - Enable drag reorder (default: true)
 * @param {boolean} [props.enableColumnPinning] - Enable pinning (default: true)
 * @param {boolean} [props.enableColumnResizing] - Enable resizing (default: true)
 * @return {JSX.Element} The table header element
 */
export function TableHeader<TData>({
  table,
  enableSorting = true,
  enableColumnReordering = true,
  enableColumnPinning = true,
  enableColumnResizing = true,
  centerHeaders = false,
  effectiveWidths,
  onColumnResize,
}: TableHeaderProps<TData>) {
  const { columnOrder, setColumnOrder, isColumnFilterActive } =
    useDataTableContext<TData>();

  // DnD sensors
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor)
  );

  // Handle drag end
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = columnOrder.indexOf(String(active.id));
        const newIndex = columnOrder.indexOf(String(over.id));

        if (oldIndex !== -1 && newIndex !== -1) {
          // Find the first reorderable position (skip fixed columns like _expander)
          const firstReorderableIndex = columnOrder.findIndex((id) => {
            const col = table.getColumn(id);
            const colMeta = col?.columnDef.meta as
              DataTableColumnMeta | undefined;
            return colMeta?.enableReordering !== false;
          });

          // Don't allow dropping before fixed columns
          if (newIndex < firstReorderableIndex) return;

          const newOrder = [...columnOrder];
          newOrder.splice(oldIndex, 1);
          newOrder.splice(newIndex, 0, String(active.id));
          setColumnOrder(newOrder);
        }
      }
    },
    [columnOrder, setColumnOrder, table]
  );

  const headerGroups = table.getHeaderGroups();
  const columnIds = table.getAllLeafColumns().map((col) => col.id);

  const headerContent = (
    <thead className={classNames(styles.tableHeader, styles.tableHeaderSticky)}>
      {headerGroups.map((headerGroup) => (
        <Fragment key={headerGroup.id}>
          <tr className={styles.tableHeaderRow}>
            {headerGroup.headers.map((header) => (
              <SortableHeaderCell
                key={header.id}
                header={header}
                enableSorting={enableSorting}
                enableColumnReordering={enableColumnReordering}
                enableColumnPinning={enableColumnPinning}
                enableColumnResizing={enableColumnResizing}
                centerHeaders={centerHeaders}
                effectiveWidth={effectiveWidths?.[header.column.id]}
                onColumnResize={onColumnResize}
              />
            ))}
          </tr>
          {isColumnFilterActive && (
            <FilterRow
              headers={headerGroup.headers}
              effectiveWidths={effectiveWidths}
            />
          )}
        </Fragment>
      ))}
    </thead>
  );

  if (!enableColumnReordering) {
    return headerContent;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={columnIds}
        strategy={horizontalListSortingStrategy}
      >
        {headerContent}
      </SortableContext>
    </DndContext>
  );
}
